/**
 * Player Intelligence — helpers puros (Fase 1 · Mi rendimiento).
 *
 * Este módulo NO renderiza nada. Contiene únicamente utilidades
 * deterministas usadas por el componente `PlayerIntelligencePerformance`.
 *
 * Reglas alineadas con la especificación aprobada:
 *   - `null` = dato no disponible → NUNCA sustituir por 0.
 *   - `0`    = cero confirmado por la fuente.
 *   - `data_quality`:
 *       'OK'      → utilizable.
 *       'PARTIAL' → utilizable (bloques pueden faltar).
 *       'REVIEW'  → NO usar como último partido publicado.
 *       ausencia  → tratar como 'REVIEW'.
 *   - KPIs por posición: en esta fase SOLO LW/RW (fixture Mario/LW).
 *     Otras posiciones quedarán fuera hasta que se apruebe su set.
 */

// ---------------------------------------------------------------------------
// KPI Registry — sin objetos anidados como métricas escalares.
// El registro define TÍTULO, FORMATO y EXTRACTOR desde el player_match.
// ---------------------------------------------------------------------------

/**
 * Formatea un número entero. `null` → devuelve `null` (el componente decide
 * ocultar). `0` → devuelve `'0'` (cero confirmado).
 */
const fmtInt = (v) => {
  if (v === null || v === undefined) return null;
  const n = Number(v);
  if (!Number.isFinite(n)) return null;
  return String(Math.round(n));
};

/**
 * Formatea un porcentaje con un decimal. `null` → devuelve `null`.
 * `0` es un valor válido.
 */
const fmtPct = (v) => {
  if (v === null || v === undefined) return null;
  const n = Number(v);
  if (!Number.isFinite(n)) return null;
  return `${n.toFixed(1)}%`;
};

/**
 * Registro cerrado de KPIs. Cualquier ampliación requerirá aprobación
 * explícita (spec Fase 1). NO añadir posiciones nuevas sin decisión.
 */
const KPI_REGISTRY = {
  'finishing_regulation.goals': {
    key: 'goals',
    label: 'Goles',
    extract: (pm) => pm?.finishing_regulation?.goals ?? null,
    format: fmtInt,
  },
  'finishing_regulation.shots': {
    key: 'shots',
    label: 'Tiros',
    extract: (pm) => pm?.finishing_regulation?.shots ?? null,
    format: fmtInt,
  },
  'passing.accuracy_pct': {
    key: 'passing_accuracy',
    label: 'Precisión pase',
    extract: (pm) => pm?.passing?.accuracy_pct ?? null,
    format: fmtPct,
  },
  'passing.progressive.completed': {
    // KPI CB/RCB/LCB — pases progresivos completados. Se lee del
    // sub-objeto canónico `passing.progressive.completed` (equivalente
    // a `passing.sub_categories.progressive.completed` — mismo dato,
    // sin duplicación).
    key: 'progressive_completed',
    label: 'Pases progresivos',
    extract: (pm) => pm?.passing?.progressive?.completed ?? null,
    format: fmtInt,
  },
  'defending.recoveries': {
    key: 'recoveries',
    label: 'Recuperaciones',
    extract: (pm) => pm?.defending?.recoveries ?? null,
    format: fmtInt,
  },
  'defending.clearances': {
    // KPI CB/RCB/LCB — despejes confirmados por la fuente.
    key: 'clearances',
    label: 'Despejes',
    extract: (pm) => pm?.defending?.clearances ?? null,
    format: fmtInt,
  },
};

/**
 * `role_config` — configuración interna estática (no editable desde Admin
 * en el MVP, según aclaración #3). Grupos aprobados en Fase 2C:
 *   - LW / RW: extremos.
 *   - CB / RCB / LCB: centrales (mismo grupo funcional).
 */
const ROLE_CONFIG = {
  LW: [
    'finishing_regulation.goals',
    'finishing_regulation.shots',
    'passing.accuracy_pct',
    'defending.recoveries',
  ],
  RW: [
    'finishing_regulation.goals',
    'finishing_regulation.shots',
    'passing.accuracy_pct',
    'defending.recoveries',
  ],
  CB: [
    'passing.accuracy_pct',
    'passing.progressive.completed',
    'defending.recoveries',
    'defending.clearances',
  ],
  RCB: [
    'passing.accuracy_pct',
    'passing.progressive.completed',
    'defending.recoveries',
    'defending.clearances',
  ],
  LCB: [
    'passing.accuracy_pct',
    'passing.progressive.completed',
    'defending.recoveries',
    'defending.clearances',
  ],
};

/**
 * Devuelve la lista de KPIs listos para renderizar según la posición
 * jugada en el partido. Cada elemento incluye:
 *   { key, label, raw, formatted }
 * Si `raw` es `null`, el componente debe OCULTAR ese KPI (sin sustituir).
 * Si `raw` es 0, se renderiza `'0'`.
 * Si la posición no está en el registro, devuelve [].
 */
export function selectKpisForPlayerMatch(playerMatch) {
  const position = (playerMatch?.player?.position || '').toUpperCase();
  const keys = ROLE_CONFIG[position];
  if (!keys) return [];
  return keys.map((k) => {
    const def = KPI_REGISTRY[k];
    if (!def) return null;
    const raw = def.extract(playerMatch);
    const formatted = def.format(raw);
    return { key: def.key, label: def.label, raw, formatted };
  }).filter(Boolean);
}

// ---------------------------------------------------------------------------
// data_quality + orden + selección del "último análisis publicado"
// ---------------------------------------------------------------------------

/**
 * Normaliza el estado de `data_quality`. Ausencia → 'REVIEW'.
 */
export function resolveDataQualityStatus(pm) {
  const s = pm?.data_quality?.status;
  if (s === 'OK' || s === 'PARTIAL' || s === 'REVIEW') return s;
  return 'REVIEW';
}

/**
 * Devuelve sólo los partidos utilizables como "último análisis publicado".
 * Excluye REVIEW y ausencia de data_quality. NO altera el orden.
 */
export function filterPublishablePlayerMatches(playerMatches) {
  if (!Array.isArray(playerMatches)) return [];
  return playerMatches.filter((pm) => {
    const st = resolveDataQualityStatus(pm);
    return st === 'OK' || st === 'PARTIAL';
  });
}

/**
 * Ordena descendente por `match.date` y desempata con `created_at` desc.
 * Elementos con `match.date` inválido caen al final.
 */
export function sortPlayerMatchesDesc(playerMatches) {
  const arr = Array.isArray(playerMatches) ? [...playerMatches] : [];
  arr.sort((a, b) => {
    const da = a?.match?.date ? Date.parse(a.match.date) : NaN;
    const dbb = b?.match?.date ? Date.parse(b.match.date) : NaN;
    const aNaN = Number.isNaN(da);
    const bNaN = Number.isNaN(dbb);
    if (aNaN && bNaN) return 0;
    if (aNaN) return 1;
    if (bNaN) return -1;
    if (dbb !== da) return dbb - da;
    const ca = a?.created_at ? Date.parse(a.created_at) : 0;
    const cb = b?.created_at ? Date.parse(b.created_at) : 0;
    return (cb || 0) - (ca || 0);
  });
  return arr;
}

/**
 * Devuelve el player_match más reciente publicable (OK|PARTIAL) o `null`
 * si no hay ninguno utilizable.
 */
export function pickLatestPublishedPlayerMatch(playerMatches) {
  const publishable = filterPublishablePlayerMatches(playerMatches);
  const sorted = sortPlayerMatchesDesc(publishable);
  return sorted[0] || null;
}

// ---------------------------------------------------------------------------
// Formato "fecha + rival" (ES). Sin librería externa. Sin locales inventadas.
// ---------------------------------------------------------------------------

/**
 * Formatea una fecha ISO (YYYY-MM-DD) al patrón "dd MMM yyyy" en es-ES.
 * Devuelve `null` si la fecha es inválida.
 */
export function formatMatchDateEs(dateIso) {
  if (!dateIso) return null;
  const t = Date.parse(dateIso);
  if (Number.isNaN(t)) return null;
  try {
    return new Date(t).toLocaleDateString('es-ES', {
      day: '2-digit', month: 'short', year: 'numeric',
    });
  } catch {
    return null;
  }
}

/**
 * Formatea un `timestamp` de vídeo (segundos, float) al patrón MM:SS.
 * Se utiliza como referencia TÉCNICA del vídeo del partido, NUNCA como
 * "minuto de partido". Devuelve `null` si la entrada no es numérica.
 */
export function formatVideoTimestamp(seconds) {
  if (seconds === null || seconds === undefined) return null;
  const n = Number(seconds);
  if (!Number.isFinite(n) || n < 0) return null;
  const total = Math.floor(n);
  const mm = String(Math.floor(total / 60)).padStart(2, '0');
  const ss = String(total % 60).padStart(2, '0');
  return `${mm}:${ss}`;
}

// ===========================================================================
// normalizePlayerMatch — capa RAW → NETSCOUTERS PLAYER MATCH
// ---------------------------------------------------------------------------
// Principio: la UI NUNCA interpreta la taxonomía del proveedor. Cualquier
// transformación deportiva (agregación, desambiguación de familias de
// eventos, redondeo, exclusiones) se hace exclusivamente aquí. Los datos
// RAW se conservan íntegramente en `source_raw` para futuras funciones
// (Análisis completo, Evolución, Informes) sin volver a importar.
//
// Reglas explícitas aplicadas (spec Fase 1B):
//   1) Interceptions family → NO promover el total 15. Desglose:
//        recoveries = 11, clearances = 1, blocks = 1, to_opponent = 2,
//        interceptions = null (subtipo inequívoco inexistente en fuente).
//        Total 15 se conserva SOLO como `source_metrics`.
//   2) Duels win_pct = won / (won + lost). Foul Conceded NO entra en el
//      denominador. Total de eventos ≠ duelos resueltos.
//   3) positions_xy: si retrieved < recorded → cobertura parcial; no se
//      genera heatmap propio. `positions_xy` normalizado = null (se
//      conserva `positions_xy_partial` sólo como metadato). Se usa
//      `heatmap_url` de la fuente.
//   4) average_position estimada por zonas → `positioning.average_position
//      = null`. El valor RAW estimado queda en `positioning.average_position_estimated`.
//   5) `minute` de eventos no es fiable en 2ª parte / tanda → NO se
//      utiliza. Se conservan `timestamp` como referencia objetiva.
//   6) Passing aggregates (attempted / completed / accuracy_pct) →
//      utilizables. Events (7/29) → marcado como parcial en metadata.
//   7) Receiving.total = null. No se recupera desde fixtures antiguos.
//   8) Finishing: Goal NO se añade automáticamente a on_target. La tanda
//      queda separada en `penalty_shootout`.
//   9) team_context: sólo se promueven player_share_pct / player_rank_team
//      cuando el team_total correspondiente está confirmado.
//  10) data_quality.status → PARTIAL. Se añade completitud por bloques.
// ===========================================================================

function coerceNumberOrNull(v) {
  if (v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function pickIfConfirmedTeamTotal(shareValue, teamTotalValue) {
  // Sólo promueve porcentajes / rankings ligados a un team_total confirmado
  // (no nulo). En caso contrario devuelve null.
  if (teamTotalValue === null || teamTotalValue === undefined) return null;
  return coerceNumberOrNull(shareValue);
}

/**
 * Accessors declarativos player-metric ↔ team_context key.
 *
 * Reglas:
 *   - Cada entrada es una función pura `(raw) => number|null` que devuelve
 *     el numerador aplicable para calcular determinísticamente el share
 *     del jugador para la key correspondiente de `team_context.team_totals`.
 *   - NO contienen ninguna condición por jugador/posición.
 *   - Si una key no está registrada aquí, NetScouters no puede calcular
 *     el share y se apoya en el RAW share con las salvaguardas habituales.
 */
const TEAM_CONTEXT_METRIC_ACCESSORS = {
  shots:             (r) => coerceNumberOrNull(r?.finishing_regulation?.shots),
  goals:             (r) => coerceNumberOrNull(r?.finishing_regulation?.goals),
  passes_attempted:  (r) => coerceNumberOrNull(r?.passing?.attempted),
  passes_completed:  (r) => coerceNumberOrNull(r?.passing?.completed),
  passes_received:   (r) => coerceNumberOrNull(r?.receiving?.total ?? r?.passing?.received),
  crosses:           (r) => coerceNumberOrNull(r?.crossing?.attempted),
  tackles:           (r) => coerceNumberOrNull(r?.defending?.tackles_attempted),
  recoveries:        (r) => coerceNumberOrNull(r?.defending?.recoveries),
  interceptions:     (r) => coerceNumberOrNull(r?.defending?.interceptions),
  duels:             (r) => coerceNumberOrNull(r?.duels?.total),
  duels_won:         (r) => coerceNumberOrNull(r?.duels?.won),
  ball_losses:       (r) => coerceNumberOrNull(r?.possession_progression?.ball_losses),
  progressive_runs:  (r) => coerceNumberOrNull(r?.possession_progression?.progressive_runs),
  final_third_entries: (r) => coerceNumberOrNull(r?.possession_progression?.final_third_entries),
};

/** Tolerancia (puntos porcentuales) para considerar discrepancia material. */
const SHARE_DISCREPANCY_TOL = 0.15;

/**
 * Proyecta `player_share_pct` de forma DETERMINISTA cuando es posible.
 *
 * Regla (independiente del ranking):
 *   Para cada key presente en `team_totals` o `player_share_pct`:
 *     - Denominador válido: `team_totals[key]` finito y > 0.
 *     - Numerador válido:   accessor(raw) finito.
 *     - Si ambos válidos:
 *         share = player_metric / team_total * 100 (1 decimal).
 *         Si el RAW share existe y difiere > SHARE_DISCREPANCY_TOL,
 *         se registra la discrepancia en metadata.
 *     - Si no puede calcularse determinísticamente pero el denominador
 *       es válido, se conserva el RAW share como fallback.
 *     - En cualquier otro caso, la key se emite como null.
 *
 * Devuelve `{ share, discrepancies }`. No contiene condiciones por jugador.
 */
function projectTeamContextShare(rawTotals, rawShare, raw) {
  const share = {};
  const discrepancies = [];
  const keys = new Set([
    ...Object.keys(rawTotals || {}),
    ...Object.keys(rawShare || {}),
  ]);
  keys.forEach((k) => {
    const total = coerceNumberOrNull(rawTotals?.[k]);
    const rawShareValue = coerceNumberOrNull(rawShare?.[k]);
    const accessor = TEAM_CONTEXT_METRIC_ACCESSORS[k];
    const playerMetric = accessor ? accessor(raw) : null;

    const denominatorValid = total !== null && total > 0;
    if (!denominatorValid) {
      share[k] = null;
      return;
    }
    if (playerMetric !== null) {
      const computed = Number(((playerMetric / total) * 100).toFixed(1));
      if (rawShareValue !== null
          && Math.abs(rawShareValue - computed) > SHARE_DISCREPANCY_TOL) {
        discrepancies.push({
          key: k,
          raw_share_pct: rawShareValue,
          computed_share_pct: computed,
          player_metric: playerMetric,
          team_total: total,
          tolerance_pp: SHARE_DISCREPANCY_TOL,
        });
      }
      share[k] = computed;
      return;
    }
    // Numerador no calculable: fallback conservador al RAW share.
    share[k] = rawShareValue;
  });
  return { share, discrepancies };
}

/**
 * Proyecta `player_rank_team` con EVIDENCIA POSITIVA DE UNIVERSO
 * COMPLETO.
 *
 * Un ranking sólo se promueve si el propio RAW aporta una señal real
 * que demuestre que el universo de jugadores usado para calcularlo es
 * completo/fiable. Sin esa evidencia, se emite `null` aunque el
 * proveedor entregue un `rank` numérico. El rank RAW permanece
 * accesible únicamente en `source_raw`.
 *
 * Evidencias positivas actualmente aceptadas (todas provienen de datos
 * REALES del RAW, no de flags inventados):
 *
 *   1. `data_quality.confirmed_full_ranking_universe_keys` contiene la
 *      key (mecanismo estándar del proveedor cuando declare esa señal).
 *   2. `data_quality.confirmed_full_ranking_universe === true` como
 *      confirmación global aplicada a todos los rankings emitidos.
 *
 * Ninguna de esas dos señales se genera artificialmente por NetScouters:
 * o llega del proveedor, o el ranking permanece `null`.
 */
function hasPositiveRankUniverseEvidence(key, dq) {
  if (!dq || typeof dq !== 'object') return false;
  if (dq.confirmed_full_ranking_universe === true) return true;
  const list = Array.isArray(dq.confirmed_full_ranking_universe_keys)
    ? dq.confirmed_full_ranking_universe_keys : null;
  if (list && list.includes(key)) return true;
  return false;
}

function projectTeamContextRank(rawTotals, rawRank, rawDataQuality) {
  const out = {};
  const keys = new Set([
    ...Object.keys(rawTotals || {}),
    ...Object.keys(rawRank || {}),
  ]);
  keys.forEach((k) => {
    const rank = coerceNumberOrNull(rawRank?.[k]);
    const total = coerceNumberOrNull(rawTotals?.[k]);
    if (rank === null || total === null) {
      out[k] = null;
      return;
    }
    if (!hasPositiveRankUniverseEvidence(k, rawDataQuality)) {
      out[k] = null;
      return;
    }
    out[k] = rank;
  });
  return out;
}

/**
 * Extrae dinámicamente las subfamilias de `passing.*` cuyo valor es un
 * par `{attempted, completed}` (progressive, smart_passes, final_third,
 * box_passes, through_passes, deep_passes, link_up_passes, …). NetScouters
 * expone todas las presentes en el RAW, sin whitelist ni condiciones por
 * jugador/posición. Los agregados escalares (`attempted`, `completed`,
 * `accuracy_pct`, etc.) se emiten aparte y no entran aquí.
 */
function extractPassingSubCategories(rawPassing) {
  const out = {};
  if (!rawPassing || typeof rawPassing !== 'object') return out;
  Object.keys(rawPassing).forEach((k) => {
    const v = rawPassing[k];
    if (v && typeof v === 'object' && !Array.isArray(v)
        && ('attempted' in v || 'completed' in v)) {
      out[k] = {
        attempted: coerceNumberOrNull(v.attempted),
        completed: coerceNumberOrNull(v.completed),
      };
    }
  });
  return out;
}

/**
 * Normaliza un evento del proveedor a la forma canónica de NetScouters.
 *
 * Reglas:
 *   - Se conserva TIMESTAMP como referencia temporal primaria.
 *   - El minuto del proveedor no es fiable en 2ª parte / tanda; se
 *     conserva en `source_minute` para trazabilidad, pero NO como
 *     minuto real del partido.
 *   - Nada se recalcula: si un campo no existe en el RAW, queda `null`.
 *   - No se inventan campos (por ejemplo `zone` sólo aparece cuando la
 *     fuente lo proporciona, típicamente en `event_details`).
 */
function normalizeEvent(rawEvent) {
  if (!rawEvent || typeof rawEvent !== 'object') return null;
  return {
    timestamp: coerceNumberOrNull(rawEvent.timestamp),
    source_minute: coerceNumberOrNull(rawEvent.minute),
    event_label: typeof rawEvent.event_label === 'string' ? rawEvent.event_label : null,
    sub_event_label:
      typeof rawEvent.sub_event_label === 'string' ? rawEvent.sub_event_label : null,
    result: rawEvent.result ?? null,
    good_bad: rawEvent.good_bad ?? null,
    x: coerceNumberOrNull(rawEvent.x),
    y: coerceNumberOrNull(rawEvent.y),
    start_x: coerceNumberOrNull(rawEvent.start_x),
    start_y: coerceNumberOrNull(rawEvent.start_y),
    end_x: coerceNumberOrNull(rawEvent.end_x),
    end_y: coerceNumberOrNull(rawEvent.end_y),
    zone: typeof rawEvent.zone === 'string' ? rawEvent.zone : null,
    duration: coerceNumberOrNull(rawEvent.duration),
    group_id: rawEvent.group_id ?? null,
    video_url:
      typeof rawEvent.video_url === 'string' ? rawEvent.video_url.trim() : null,
  };
}

/**
 * Normaliza una key_action del proveedor. La forma del RAW usa `type`/
 * `subtype` en lugar de `event_label`/`sub_event_label`; se preserva la
 * semántica sin promoverla a "mejor jugada": es un subconjunto objetivo
 * del proveedor útil para navegación en el futuro Análisis completo.
 */
function normalizeKeyAction(rawKa) {
  if (!rawKa || typeof rawKa !== 'object') return null;
  return {
    timestamp: coerceNumberOrNull(rawKa.timestamp),
    source_minute: coerceNumberOrNull(rawKa.minute),
    // Se normaliza a la misma taxonomía de `event_details`.
    event_label: typeof rawKa.type === 'string' ? rawKa.type : null,
    sub_event_label: typeof rawKa.subtype === 'string' ? rawKa.subtype : null,
    result: rawKa.result ?? null,
    good_bad: rawKa.good_bad ?? null,
    x: coerceNumberOrNull(rawKa.x),
    y: coerceNumberOrNull(rawKa.y),
    zone: typeof rawKa.zone === 'string' ? rawKa.zone : null,
    video_url:
      typeof rawKa.video_url === 'string' ? rawKa.video_url.trim() : null,
  };
}

/**
 * Determina la completitud de un array de eventos comparándolo con el
 * agregado esperado indicado por la fuente. Regla genérica:
 *
 *   1. agregado null/undefined + events 0                       → unavailable
 *   2. agregado null/undefined + events > 0                     → partial
 *      (hay eventos pero no hay universo declarado por la fuente)
 *   3. agregado === 0 confirmado + events 0                     → complete
 *      (TRUE ZERO — el proveedor confirma cero: nada que recuperar)
 *   4. agregado === 0 confirmado + events > 0                   → partial
 *      (inconsistencia RAW: el agregado dice cero pero hay eventos)
 *   5. agregado > 0 + events.length === agregado                → complete
 *   6. agregado > 0 + 0 < events.length < agregado              → partial
 *   7. agregado > 0 + events.length === 0                       → unavailable
 */
function eventsCompleteness(events, expectedCount) {
  const n = Array.isArray(events) ? events.length : 0;
  if (expectedCount === null || expectedCount === undefined) {
    return n > 0 ? 'partial' : 'unavailable';
  }
  if (expectedCount === 0) {
    return n === 0 ? 'complete' : 'partial';
  }
  if (n >= expectedCount) return 'complete';
  return n > 0 ? 'partial' : 'unavailable';
}

/**
 * Transforma un `raw` player match del proveedor en el contrato interno
 * de NetScouters consumido por `PlayerIntelligencePerformance` y futuras
 * pantallas de Player Intelligence.
 *
 * Devuelve `null` si `raw` no es un objeto válido.
 */
export function normalizePlayerMatch(raw, options = {}) {
  if (!raw || typeof raw !== 'object') return null;
  const rawMatch = raw.match || {};
  const rawPlayer = raw.player || {};
  const rawPositioning = raw.positioning || {};
  const rawPassing = raw.passing || {};
  const rawReceiving = raw.receiving || {};
  const rawDefending = raw.defending || {};
  const rawDuels = raw.duels || {};
  const rawFinishing = raw.finishing_regulation || {};
  const rawShootout = raw.penalty_shootout || {};
  const rawCrossing = raw.crossing || {};
  const rawTeamContext = raw.team_context || {};
  const rawTeamTotals = rawTeamContext.team_totals || {};
  const rawShare = rawTeamContext.player_share_pct || {};
  const rawRank = rawTeamContext.player_rank_team || {};
  const rawQuality = raw.data_quality || {};

  // ---- Interceptions family desambiguation --------------------------------
  const interceptionsBreakdown =
    raw?.additional_available_data?.interceptions_sub_event_breakdown || {};
  const familyTotal =
    typeof rawDefending.interceptions === 'number'
      ? rawDefending.interceptions
      : Object.values(interceptionsBreakdown).reduce(
        (a, b) => a + (Number(b) || 0),
        0,
      );

  // ---- Positioning: coverage flag -----------------------------------------
  const positionsRecorded = coerceNumberOrNull(rawPositioning.positions_recorded);
  const positionsRetrieved = coerceNumberOrNull(rawPositioning.positions_retrieved);
  const coverageIsFull =
    positionsRecorded !== null &&
    positionsRetrieved !== null &&
    positionsRetrieved >= positionsRecorded;

  const normalizedPositionsXy = coverageIsFull
    ? (Array.isArray(rawPositioning.positions_xy)
      ? rawPositioning.positions_xy
        .map((p) => [coerceNumberOrNull(p?.x), coerceNumberOrNull(p?.y)])
        .filter(([x, y]) => x !== null && y !== null)
      : null)
    : null;

  // ---- Duels win_pct (won / (won + lost)) ---------------------------------
  const duelsWon = coerceNumberOrNull(rawDuels?.won ?? rawDuels?.offensive?.won);
  const duelsLost = coerceNumberOrNull(rawDuels?.lost ?? rawDuels?.offensive?.lost);
  const duelsResolved =
    duelsWon !== null && duelsLost !== null ? duelsWon + duelsLost : null;
  const duelsWinPct =
    duelsWon !== null && duelsLost !== null && (duelsWon + duelsLost) > 0
      ? Number(((duelsWon / (duelsWon + duelsLost)) * 100).toFixed(1))
      : null;
  const foulsConceded = coerceNumberOrNull(
    raw?.additional_available_data?.duels_sub_event_breakdown?.['Foul Conceded'],
  );
  const duelsTotalEvents = coerceNumberOrNull(rawDuels?.total ?? rawDuels?.offensive?.total);

  // ---- Event normalization (Fase 1C) --------------------------------------
  // Todos los eventos objetivos que la UI puede consumir sin tocar
  // `source_raw`. Se normalizan preservando `timestamp` y renombrando
  // `minute` → `source_minute` (Regla 7).
  const rawEventDetails = Array.isArray(raw.event_details) ? raw.event_details : [];
  const rawKeyActions = Array.isArray(raw.key_actions) ? raw.key_actions : [];
  const eventDetails = rawEventDetails
    .map(normalizeEvent)
    .filter((e) => e !== null);
  const keyActions = rawKeyActions
    .map(normalizeKeyAction)
    .filter((e) => e !== null);
  const passingEvents = Array.isArray(rawPassing.events)
    ? rawPassing.events.map(normalizeEvent).filter(Boolean) : [];
  const finishingEvents = Array.isArray(rawFinishing.events)
    ? rawFinishing.events.map(normalizeEvent).filter(Boolean) : [];
  const shootoutEvents = Array.isArray(rawShootout.events)
    ? rawShootout.events.map(normalizeEvent).filter(Boolean) : [];
  const defendingEvents = Array.isArray(rawDefending.events)
    ? rawDefending.events.map(normalizeEvent).filter(Boolean) : [];
  const crossingEvents = Array.isArray(rawCrossing.events)
    ? rawCrossing.events.map(normalizeEvent).filter(Boolean) : [];
  const receivingEvents = Array.isArray(rawReceiving.events)
    ? rawReceiving.events.map(normalizeEvent).filter(Boolean) : [];

  // Completitud por familia — comparación contra agregados confirmados.
  const passingExpected = coerceNumberOrNull(rawPassing.attempted);
  const finishingExpected = coerceNumberOrNull(rawFinishing.shots);
  const shootoutExpected = coerceNumberOrNull(rawShootout.shots);
  // Familia defensiva: sumatorio de agregados esperados (recoveries +
  // interceptions_family + tackles_attempted + clearances + blocks +
  // fouls_committed) es ambiguo; usamos el propio agregado de la
  // familia interceptions + tackles_attempted como referencia
  // conservadora, marcando el bloque a lo sumo como 'partial' cuando
  // exista información. El RAW ya declara que este bloque fue truncado.
  const defendingExpected = (() => {
    const inter = coerceNumberOrNull(familyTotal);
    const tackAtt = coerceNumberOrNull(rawDefending.tackles_attempted);
    if (inter === null && tackAtt === null) return null;
    return (inter || 0) + (tackAtt || 0);
  })();
  const crossingExpected = coerceNumberOrNull(rawCrossing.attempted);
  const receivingExpected = coerceNumberOrNull(rawReceiving.total);

  const shareProjection = projectTeamContextShare(rawTeamTotals, rawShare, raw);
  const normalized = {
    // Identificación estable (aporta el importador; en fixture se inyecta).
    player_match_id: options.player_match_id ?? null,
    player_id: options.player_id ?? null,

    match: {
      date: rawMatch.date ?? null,
      opponent: rawMatch.opponent ?? null,
      team: typeof rawMatch.team === 'string' ? rawMatch.team : null,
      video_url: typeof rawMatch.video_url === 'string' ? rawMatch.video_url.trim() : null,
      source_match_id: rawMatch.source_match_id ?? null,
      // Timestamps de periodos: conservados para calcular minutos fiables
      // en futuras fases. NO se utilizan como "minuto de partido" en la UI.
      periods: {
        first_half_start: rawMatch.first_half_start ?? null,
        first_half_end: rawMatch.first_half_end ?? null,
        second_half_start: rawMatch.second_half_start ?? null,
        second_half_end: rawMatch.second_half_end ?? null,
        penalties_start: rawMatch.penalties_start ?? null,
        penalties_end: rawMatch.penalties_end ?? null,
      },
      score_regulation: rawMatch.score_regulation ?? null,
      match_duration: rawMatch.match_duration ?? null,
    },
    player: {
      // Posición JUGADA en este partido (no la principal de la ficha maestra).
      position: rawPlayer.position ?? null,
      minutes_played: coerceNumberOrNull(rawPlayer.minutes_played),
      shirt_number: coerceNumberOrNull(rawPlayer.shirt_number),
      starter: typeof rawPlayer.starter === 'boolean' ? rawPlayer.starter : null,
      // dominant_foot se conserva sólo para referencia; la ficha maestra
      // sigue siendo la fuente oficial.
      dominant_foot: rawPlayer.dominant_foot ?? null,
    },
    finishing_regulation: {
      shots: coerceNumberOrNull(rawFinishing.shots),
      goals: coerceNumberOrNull(rawFinishing.goals),
      on_target: coerceNumberOrNull(rawFinishing.on_target),
      off_target: coerceNumberOrNull(rawFinishing.off_target),
      blocked: coerceNumberOrNull(rawFinishing.blocked),
      inside_box: coerceNumberOrNull(rawFinishing.inside_box),
      outside_box: coerceNumberOrNull(rawFinishing.outside_box),
      conversion_pct: coerceNumberOrNull(rawFinishing.conversion_pct),
      xg: coerceNumberOrNull(rawFinishing.xg),
      xa: coerceNumberOrNull(rawFinishing.xa),
      chances_created: coerceNumberOrNull(rawFinishing.chances_created),
      events: finishingEvents,
    },
    penalty_shootout: {
      shots: coerceNumberOrNull(rawShootout.shots),
      goals: coerceNumberOrNull(rawShootout.goals),
      events: shootoutEvents,
    },
    passing: {
      // Aggregates completos según la fuente.
      attempted: coerceNumberOrNull(rawPassing.attempted),
      completed: coerceNumberOrNull(rawPassing.completed),
      failed: coerceNumberOrNull(rawPassing.failed),
      accuracy_pct: coerceNumberOrNull(rawPassing.accuracy_pct),
      forward: coerceNumberOrNull(rawPassing.forward),
      backward: coerceNumberOrNull(rawPassing.backward),
      lateral: coerceNumberOrNull(rawPassing.lateral),
      avg_length_m: coerceNumberOrNull(rawPassing.avg_length_m),
      avg_completed_length_m: coerceNumberOrNull(rawPassing.avg_completed_length_m),
      avg_failed_length_m: coerceNumberOrNull(rawPassing.avg_failed_length_m),
      // progressive/crossing/duels.offensive requieren definir el valor
      // escalar antes de promoverse — quedan expuestos como objetos
      // para futuras fases pero NO se utilizan como métrica visible.
      progressive: {
        attempted: coerceNumberOrNull(rawPassing?.progressive?.attempted),
        completed: coerceNumberOrNull(rawPassing?.progressive?.completed),
      },
      // Subfamilias dinámicas (progressive, smart_passes, final_third,
      // box_passes, through_passes, deep_passes, link_up_passes, …).
      // Se emiten TODAS las presentes en el RAW, sin whitelist ni
      // condiciones por jugador/posición.
      sub_categories: extractPassingSubCategories(rawPassing),
      key_passes: coerceNumberOrNull(rawPassing.key_passes),
      assists: coerceNumberOrNull(rawPassing.assists),
      events: passingEvents,
    },
    receiving: {
      // Explicit: no recuperado por la fuente. NO recuperar de fixtures
      // anteriores.
      total: coerceNumberOrNull(rawReceiving.total),
      defensive_third: coerceNumberOrNull(rawReceiving.defensive_third),
      middle_third: coerceNumberOrNull(rawReceiving.middle_third),
      attacking_third: coerceNumberOrNull(rawReceiving.attacking_third),
      inside_box: coerceNumberOrNull(rawReceiving.inside_box),
      under_pressure: coerceNumberOrNull(rawReceiving.under_pressure),
      events: receivingEvents,
    },
    defending: {
      // Regla 4: NO se promueve el total 15 de la familia. Se exponen los
      // subtipos deportivos individuales y se marca `interceptions` como
      // null hasta que exista un subtipo que represente inequívocamente
      // una intercepción deportiva.
      recoveries: coerceNumberOrNull(rawDefending.recoveries),
      clearances: coerceNumberOrNull(rawDefending.clearances),
      blocks: coerceNumberOrNull(rawDefending.blocks),
      to_opponent: coerceNumberOrNull(interceptionsBreakdown['To Opponent']),
      interceptions: null,
      tackles: {
        attempted: coerceNumberOrNull(rawDefending.tackles_attempted),
        won: coerceNumberOrNull(rawDefending.tackles_won),
      },
      pressures: coerceNumberOrNull(rawDefending.pressures),
      high_press_actions: coerceNumberOrNull(rawDefending.high_press_actions),
      fouls_committed: coerceNumberOrNull(rawDefending.fouls_committed),
      events: defendingEvents,
    },
    duels: {
      // Ver Regla 5.
      total_events: duelsTotalEvents,
      resolved: duelsResolved,
      won: duelsWon,
      lost: duelsLost,
      fouls_conceded: foulsConceded,
      win_pct: duelsWinPct,
    },
    crossing: {
      attempted: coerceNumberOrNull(rawCrossing.attempted),
      completed: coerceNumberOrNull(rawCrossing.completed),
      events: crossingEvents,
    },
    positioning: {
      // Regla 3: coordenadas visibles sólo con cobertura completa.
      positions_xy: normalizedPositionsXy,
      // Se conservan las 42 coordenadas parciales sólo como metadato de
      // futuras funciones (nunca consumidas por la UI actual).
      positions_xy_partial: coverageIsFull ? null : (rawPositioning.positions_xy || null),
      positions_recorded: positionsRecorded,
      positions_retrieved: positionsRetrieved,
      coverage:
        positionsRecorded === null || positionsRetrieved === null
          ? 'unknown'
          : coverageIsFull ? 'full' : 'partial',
      heatmap_url:
        typeof rawPositioning.heatmap_url === 'string'
          ? rawPositioning.heatmap_url.trim()
          : null,
      // Regla 6: la media estimada por zonas NO es una posición media
      // objetiva. Se preserva sólo como valor estimado.
      average_position: null,
      average_position_estimated: rawPositioning.average_position ?? null,
      thirds_pct: rawPositioning.thirds_pct ?? null,
      sides_pct: rawPositioning.sides_pct ?? null,
      most_active_zones: Array.isArray(rawPositioning.most_active_zones)
        ? rawPositioning.most_active_zones
        : null,
    },
    team_context: {
      scope: rawTeamContext.scope ?? null,
      // Promoción DINÁMICA e INDEPENDIENTE de share y rank.
      // - Share: DETERMINISTA cuando player_metric y team_total son
      //          fiables y compatibles (share = player/total * 100).
      //          El RAW share sólo se utiliza como fallback si el
      //          numerador no está disponible.
      // - Rank : requiere EVIDENCIA POSITIVA de universo completo
      //          declarada por el proveedor. Sin evidencia, `null`.
      player_share_pct: shareProjection.share,
      player_rank_team: projectTeamContextRank(rawTeamTotals, rawRank, rawQuality),
    },
    sequences: {
      participated: coerceNumberOrNull(raw?.team_involvement?.pass_sequences_participated),
      passes_given: coerceNumberOrNull(raw?.team_involvement?.passes_given_in_sequences),
      passes_received: coerceNumberOrNull(raw?.team_involvement?.passes_received_in_sequences),
      longest: coerceNumberOrNull(raw?.team_involvement?.longest_sequence),
      team_total_sequences: coerceNumberOrNull(raw?.team_involvement?.team_total_sequences),
      top_sequences: Array.isArray(raw?.team_involvement?.top_sequences)
        ? raw.team_involvement.top_sequences
        : [],
    },
    // Fase 1C — vistas normalizadas de eventos objetivos. Las pantallas
    // futuras (Análisis completo, Evolución, Informes) deben leerlas
    // desde aquí y NUNCA desde `source_raw`.
    event_details: eventDetails,
    key_actions: keyActions,
    // Metadata bruta preservada — no visible en UI, útil para fases futuras.
    source_metrics: {
      interceptions_family_total: familyTotal,
      duels_sub_event_breakdown:
        raw?.additional_available_data?.duels_sub_event_breakdown || null,
      interceptions_sub_event_breakdown: interceptionsBreakdown,
      minute_reliable: false, // Regla 7
    },

    // Estado global + completitud por bloques.
    data_quality: {
      status: 'PARTIAL', // Regla 12
      issues: Array.isArray(rawQuality.issues) ? [...rawQuality.issues] : [],
      unavailable_fields: Array.isArray(rawQuality.unavailable_fields)
        ? [...rawQuality.unavailable_fields] : [],
      partial_or_truncated_fields: Array.isArray(rawQuality.partial_or_truncated_fields)
        ? [...rawQuality.partial_or_truncated_fields] : [],
      // Discrepancias share-RAW ↔ share-calculado. Vacío si no hay.
      // No altera valores deportivos; queda como metadata trazable.
      share_discrepancies: shareProjection.discrepancies,
      block_completeness: {
        match: 'complete',
        player: 'usable',
        positioning: coverageIsFull ? 'usable' : 'partial',
        passing_aggregates: 'complete',
        passing_events: eventsCompleteness(passingEvents, passingExpected),
        // `receiving` (agregado) es utilizable ⇔ hay `receiving.total`
        // confirmado. Puede ser 'usable' aunque los eventos detallados
        // sean parciales. Se separa explícitamente en `receiving_events`.
        receiving: receivingExpected !== null ? 'usable' : 'unavailable',
        receiving_events: eventsCompleteness(receivingEvents, receivingExpected),
        finishing: 'usable',
        finishing_events: eventsCompleteness(finishingEvents, finishingExpected),
        penalty_shootout_events: eventsCompleteness(shootoutEvents, shootoutExpected),
        defending_aggregates: 'partial',
        defending_events: eventsCompleteness(defendingEvents, defendingExpected),
        // `duels` (agregado) es utilizable ⇔ won y lost están confirmados.
        // Ninguna estimación (p. ej. `estimated_duel_count` presente en
        // `additional_available_data`) se promueve.
        duels: (duelsWon !== null && duelsLost !== null) ? 'usable' : 'unavailable',
        crossing_events: eventsCompleteness(crossingEvents, crossingExpected),
        team_context: 'partial',
        sequences: 'partial',
        // event_details ≈ suma de todas las familias de eventos objetivos
        // registradas por el proveedor. Se considera 'partial' cuando
        // alguna de las familias detalladas está a su vez parcial.
        event_details: (() => {
          const flags = [
            eventsCompleteness(passingEvents, passingExpected),
            eventsCompleteness(finishingEvents, finishingExpected),
            eventsCompleteness(defendingEvents, defendingExpected),
            eventsCompleteness(crossingEvents, crossingExpected),
            eventsCompleteness(shootoutEvents, shootoutExpected),
          ];
          if (flags.every((f) => f === 'complete')) return 'complete';
          if (flags.every((f) => f === 'unavailable')) return 'unavailable';
          return 'partial';
        })(),
        // key_actions: el proveedor entrega una lista discreta objetiva.
        // Se marca complete si hay lista y unavailable si no.
        key_actions: keyActions.length > 0 ? 'complete' : 'unavailable',
      },
    },

    // RAW completo preservado para futuras fases (Análisis completo,
    // Evolución, Informes). NUNCA se muestra en "Mi rendimiento".
    source_raw: raw,

    created_at: options.created_at ?? (rawMatch.date ? `${rawMatch.date}T00:00:00Z` : null),
  };

  return normalized;
}
